import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { useTranslation } from "react-i18next";
import { ReactComponent as Accounts } from "../../assets/SideBarIcons/accounts.svg";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import QrCodeScannerOutlined from "@mui/icons-material/QrCodeScannerOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Shield from "../../assets/shield.png";
import { ReactComponent as JobCardIcon } from "assets/Modal/jobcard.svg";
import { ReactComponent as InvoiceIcon } from "assets/Modal/invoice.svg";
import { ReactComponent as WorkOrderIcon } from "assets/Modal/workorder.svg";
import { ReactComponent as InspectionIcon } from "assets/SideBarIcons/inspection.svg";
import { ReactComponent as VehicleIcon } from "assets/SideBarIcons/vehicle.svg";
import { ReactComponent as UsersIcon } from "assets/SideBarIcons/users.svg";
import { ReactComponent as StationIcon } from "assets/SideBarIcons/station.svg";
import { ReactComponent as StoreIcon } from "assets/SideBarIcons/store.svg";
import { ReactComponent as CustomerIcon } from "assets/SideBarIcons/customer.svg";
import { ReactComponent as BusinessIcon } from "assets/SideBarIcons/business.svg";
import AccountSettingsTabs from "./AccountSettingsTabs";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useGetSubscriptionPlanByBusinessService } from "subModule/src/services/subscriptionplans/useSubscriptionPlan";
import { GetSubscriptionPlanByBusiness } from "subModule/src/services/subscriptionplans/interface";
import Loader from "layout/Loader";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { selectUserInfo } from "features/user/userSlice";
import { selectSubscriptionPlanMeta } from "features/subscription/subscriptionSlice";
import { Navigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import { SubscriptionPlanHistory } from "pages/Business/SubscriptionPlanHistory";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { SubscriptionPlanChange } from "pages/Business/SubscriptionPlanChange";
import {
  BusinessProfileService,
  SubscriptionPlanProfileService,
} from "subModule/src/core/services";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import SimpleModal from "common/Form/Form";

export default function SubscriptionPlanUsage() {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const loggedInUserData = useSelector(selectUserInfo);
  const { loaded: subscriptionsLoaded, hasPlan } = useSelector(
    selectSubscriptionPlanMeta,
  );
  const businessId = loggedInUserData?.business?.id;
  const userType = Number(loggedInUserData?.type);
  const isAllowedUserType = userType === 100 || userType === 110;
  const canAccessSubscriptionPlanUsage = isAllowedUserType && hasPlan;
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [data, setData] = useState<any>(null);
  const [billingHistoryModal, setBillingHistoryModal] = useState(false);
  const [changePlanModal, setChangePlanModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [autoRenewUpdating, setAutoRenewUpdating] = useState(false);
  const { isRefetching: planFetching, refetch: planRefetch } =
    useGetSubscriptionPlanByBusinessService(businessId);
  const isLargeScreen = useMediaQuery("@media (min-width: 1200px)");
  const subscriptionControls = false;
  useEffect(() => {
    // Wait until getplan access is confirmed so refresh doesn't fire a canceled fetch.
    if (businessId && canAccessSubscriptionPlanUsage) fetchData();
  }, [businessId, canAccessSubscriptionPlanUsage]);

  const fetchData = async () => {
    await planRefetch()
      .then((res: GetSubscriptionPlanByBusiness | any) => {
        if (res?.isError || res?.failureReason || res?.error) {
          const err = res?.error || res?.failureReason;
          if (
            err?.name === "CanceledError" ||
            err?.name === "AbortError" ||
            err?.code === "ERR_CANCELED" ||
            err?.message === "Canceled"
          ) {
            return;
          }
          setData(null);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message ||
                err?.data?.message ||
                res?.data?.message ||
                "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          return;
        }
        if (res?.data?.success) {
          setData(res?.data?.data || null);
        } else {
          setData(null);
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        if (
          err?.name === "CanceledError" ||
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED" ||
          err?.message === "Canceled"
        ) {
          return;
        }
        setData(null);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };

  const handleSuspendNow = (action: number) => {
    setSuspending(true);
    SubscriptionPlanProfileService.suspendSubscriptionPlan({
      planId: Number(data?.id),
      status: action,
    })
      .then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                (action === 5 ? t("suspended") : t("cancelled")) +
                " " +
                t("successfully")
              }
            />,
            { variant: "success" },
          );
          fetchData();
        } else {
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setSuspending(false));
  };

  const handleResumeNow = () => {
    if (!businessId) return;
    setSuspending(true);
    BusinessProfileService.changeStatusBusiness(
      Number(businessId),
      "active",
      {},
    )
      .then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("active") + " " + t("successfully")}
            />,
            { variant: "success" },
          );
          fetchData();
        } else {
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setSuspending(false));
  };

  const handleAutoRenewChange = (autoRenew: boolean) => {
    const targetBusinessId = Number(data?.businessId || businessId);
    if (!targetBusinessId) return;
    const previousValue = Boolean(data?.autoRenewal);
    setData((prev: any) =>
      prev ? { ...prev, autoRenewal: autoRenew } : prev,
    );
    setAutoRenewUpdating(true);
    SubscriptionPlanProfileService.updateAutoRenew(targetBusinessId, autoRenew)
      .then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                autoRenew
                  ? t("autoRenewalEnabledSuccessfully")
                  : t("autoRenewalDisabledSuccessfully")
              }
            />,
            { variant: "success" },
          );
          fetchData();
        } else {
          setData((prev: any) =>
            prev ? { ...prev, autoRenewal: previousValue } : prev,
          );
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        setData((prev: any) =>
          prev ? { ...prev, autoRenewal: previousValue } : prev,
        );
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setAutoRenewUpdating(false));
  };

  const isSuspended = data?.status === 5;
  const isCancelled = data?.status === 4;
  const BYTES_PER_GB = 1024 * 1024 * 1024;
  const formatBytesToGb = (bytes: number) => {
    const gb = bytes / BYTES_PER_GB;
    return Number.isInteger(gb) ? String(gb) : gb.toFixed(1);
  };
  const feature2201 = data?.plan?.planFeature?.find(
    (f: any) => Number(f?.featureId) === 2201,
  );
  const type1Features = useMemo(() => {
    const features = data?.plan?.planFeature;
    if (!Array.isArray(features)) return [];
    return features.filter(
      (feature: any) =>
        Number(feature?.type) === 1 && feature?.enable !== false,
    );
  }, [data]);
  const topType1Features = useMemo(
    () => type1Features.slice(0, 6),
    [type1Features],
  );
  const bottomType1Features = useMemo(
    () => type1Features.slice(6),
    [type1Features],
  );

  const getFeatureIcon = (feature: any): ReactNode => {
    const iconClass = "w-[22px] h-[22px]";
    switch (Number(feature?.featureId)) {
      case 101:
        return <UsersIcon className={iconClass} />;
      case 201:
        return <StationIcon className={iconClass} />;
      case 301:
        return <StoreIcon className={iconClass} />;
      case 401:
        return <VehicleIcon className={iconClass} />;
      case 501:
        return <JobCardIcon className={iconClass} />;
      case 601:
        return <WorkOrderIcon className={iconClass} />;
      case 701:
        return (
          <DescriptionOutlinedIcon sx={{ fontSize: 21, color: "#111827" }} />
        );
      case 901:
        return <InspectionIcon className={iconClass} />;
      case 1001:
        return <InvoiceIcon className={iconClass} />;
      case 2101:
        return (
          <QrCodeScannerOutlined sx={{ fontSize: 21, color: "#111827" }} />
        );
      case 4201:
        return <CustomerIcon className={iconClass} />;
      case 4301:
        return <BusinessIcon className={iconClass} />;
      case 4401:
        return (
          <LocalShippingOutlinedIcon sx={{ fontSize: 21, color: "#111827" }} />
        );
      default:
        return <WidgetsOutlinedIcon sx={{ fontSize: 21, color: "#111827" }} />;
    }
  };

  const renderType1UsageWidget = (feature: any) => {
    const usage = Number(feature?.usage ?? 0);
    const maxLimit = Number(feature?.maxLimit ?? 0);
    const isUnlimited = Number(feature?.maxLimit) === -1;
    const percentage =
      !isUnlimited && maxLimit > 0 ? (usage / maxLimit) * 100 : 0;
    const progressValue = percentage >= 100 ? 100 : percentage;
    const progressColor =
      percentage < 25 ? "#22C55E" : percentage <= 75 ? "#F97316" : "#DC2626";
    const featureName = feature?.name || "---";
    const rawDescription = feature?.description?.trim();
    const featureDescription = rawDescription
      ? t(rawDescription)
      : t("featureUsageDescription", { name: t(featureName) });

    return (
      <Box
        key={feature?.featureId || feature?.id}
        className="bg-white font-Saira rounded-[8px] border border-[#E5E7EB] p-[16px] flex flex-col gap-[10px] h-full"
      >
        <Box className="flex items-center justify-between gap-[8px]">
          <Box className="flex items-center gap-[10px] min-w-0">
            <Box className="w-[40px] h-[40px] rounded-[8px] border border-[#E5E7EB] flex items-center justify-center shrink-0">
              {getFeatureIcon(feature)}
            </Box>
            <Box className="flex flex-col gap-[2px] min-w-0">
              <Typography className="font-semibold text-[14px] text-[#111827] leading-tight text-left font-Saira truncate">
                {t(featureName)}
              </Typography>
              <Typography className="text-[11px] text-[#6B7280] font-medium leading-snug text-left font-Saira line-clamp-2">
                {featureDescription}
              </Typography>
            </Box>
          </Box>
          {isUnlimited ? (
            <Typography
              className="text-[11px] font-semibold whitespace-nowrap font-Saira shrink-0"
              sx={{ color: "#22C55E" }}
            >
              {t("Unlimited")}
            </Typography>
          ) : (
            <Typography
              className="text-[11px] font-semibold whitespace-nowrap font-Saira shrink-0"
              sx={{ color: progressColor }}
            >
              {`${percentage > 100 ? 100 : Number(percentage.toFixed(0))}% ${t("usage")}`}
            </Typography>
          )}
        </Box>
        <Box className="flex items-center gap-[12px]">
          <Typography className="font-semibold text-[14px] text-[#111827] leading-tight font-Saira text-left whitespace-nowrap">
            {`${usage} / ${isUnlimited ? t("Unlimited") : maxLimit}`}
          </Typography>
          {isUnlimited ? null : (
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 4,
                bgcolor: "#E5E7EB",
                "& .MuiLinearProgress-bar": {
                  bgcolor: progressColor,
                  borderRadius: 4,
                },
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  const businessName = loggedInUserData?.business?.title || "";

  if (!isAllowedUserType) {
    return <Navigate to="/account-settings" replace />;
  }
  if (!subscriptionsLoaded) {
    return <Loader />;
  }
  if (!canAccessSubscriptionPlanUsage) {
    return <Navigate to="/account-settings" replace />;
  }

  return (
    <>
      <ComponentHeaderTwo
        showBtn={false}
        showSaveBtn={false}
        heading={t("accountSettings")}
        icon={<Accounts height={28} width={28} />}
        btnText="back"
        btnType="back"
      />
      <div className="flex">
        <AccountSettingsTabs selectedIndex={3} />
        <SimpleModal
          visable={true}
          title={
            businessName
              ? `${t("businessSubscription")}: ${businessName}`
              : t("subscriptionPlanUsage")
          }
          isButtonVisable={false}
          notVisableBackbtn={true}
          content={
            <Box>
              {!businessId || planFetching ? (
                <Loader />
              ) : !data ? (
                <Typography
                  className="font-Saira font-[500] text-[15px] italic text-center w-full py-8"
                  sx={{ color: themeColors?.primary }}
                >
                  {t("noRecordFound")}
                </Typography>
              ) : (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Box
                      className="flex justify-between items-center font-Saira rounded-[8px] py-[10px] px-[20px]"
                      style={{ backgroundColor: themeColors?.button }}
                    >
                      <Box
                        className="font-semibold text-[15px]"
                        style={{ color: themeColors.primary }}
                      >
                        {t("planUsage")}
                      </Box>
                      {data?.status ? (
                        <Box className="bg-white rounded-[20px] py-[6px] px-[14px] font-Saira flex items-center gap-[12px]">
                          <Box
                            className={`rounded-[50px] text-[11px] font-semibold uppercase px-[10px] py-[2px]
                              ${
                                data?.status === 1
                                  ? "bg-[#D1FAE5] text-[#065F46]"
                                  : data?.status === 2
                                    ? "bg-[#C5C5C5] text-[#040404]"
                                    : data?.status === 3
                                      ? "bg-[#C4C5E8] text-[#0007B4]"
                                      : data?.status === 4
                                        ? "bg-[#E9C9C9] text-[#BA1A1A]"
                                        : data?.status === 5
                                          ? "bg-[#F6D7D0] text-[#FD603B]"
                                          : "bg-[#C5C5C5] text-[#040404]"
                              }
                            `}
                          >
                            {data?.status === 1
                              ? t("active")
                              : data?.status === 2
                                ? t("inactive")
                                : data?.status === 3
                                  ? t("trial")
                                  : data?.status === 4
                                    ? t("cancelled")
                                    : data?.status === 5
                                      ? t("suspended")
                                      : "---"}
                          </Box>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ borderColor: "#E5E7EB", my: "2px" }}
                          />
                          <Box className="flex items-center gap-[8px]">
                            <Typography className="text-[12px] text-[#111827] font-medium whitespace-nowrap leading-none">
                              {t("autoRenewal")}
                            </Typography>
                            <Switch
                              checked={Boolean(data?.autoRenewal)}
                              disabled={autoRenewUpdating}
                              onChange={(_, checked) =>
                                handleAutoRenewChange(checked)
                              }
                              sx={{
                                width: 42,
                                height: 22,
                                p: 0,
                                "& .MuiSwitch-switchBase": {
                                  p: "2px",
                                  color: "#fff",
                                  "&.Mui-checked": {
                                    transform: "translateX(20px)",
                                    color: "#fff",
                                    "& + .MuiSwitch-track": {
                                      bgcolor: themeColors?.secondary,
                                      opacity: 1,
                                    },
                                  },
                                },
                                "& .MuiSwitch-thumb": {
                                  width: 18,
                                  height: 18,
                                  boxShadow: "none",
                                  background: "#fff",
                                },
                                "& .MuiSwitch-track": {
                                  borderRadius: "21px",
                                  bgcolor: "#C5C5C5",
                                  opacity: 1,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ alignSelf: "flex-start" }}>
                    <Box className="flex flex-col gap-[10px] w-full">
                      <Box className="bg-white font-Saira rounded-[8px] border border-[#E5E7EB] p-[20px] flex flex-col gap-[16px]">
                        <Box className="flex items-center justify-between gap-[8px]">
                          <Box className="flex items-center gap-[6px]">
                            <img src={Shield} alt="" />
                            <Box className="font-semibold text-[13px] text-[#111827] leading-none">
                              {t("planDetails")}
                            </Box>
                          </Box>
                          {data?.plan?.name ? (
                            <Tooltip placement="top" title={data.plan.name}>
                              <Typography className="rounded-[50px] bg-[#111827] text-white text-[9px] font-semibold px-[10px] py-[3px] tracking-[0.04em] overflow-hidden text-ellipsis whitespace-nowrap w-[100px]">
                                {`${data.plan.name}`}
                              </Typography>
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box className="flex flex-col gap-[14px]">
                          {Object.entries({
                            billingCycle:
                              data?.plan?.billingCycle === 1
                                ? t("monthly")
                                : data?.plan?.billingCycle === 2
                                  ? t("yearly")
                                  : data?.plan?.billingCycle === 3
                                    ? t("weekly")
                                    : "---",
                            startDate: data?.startDate
                              ? new Intl.DateTimeFormat(i18n.language, {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  numberingSystem:
                                    i18n.language === "ur" ||
                                    i18n.language === "ar"
                                      ? "arab"
                                      : "latn",
                                }).format(new Date(data.startDate))
                              : "---",
                            endDate: data?.endDate
                              ? new Intl.DateTimeFormat(i18n.language, {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  numberingSystem:
                                    i18n.language === "ur" ||
                                    i18n.language === "ar"
                                      ? "arab"
                                      : "latn",
                                }).format(new Date(data.endDate))
                              : "---",
                            price: data?.plan?.price
                              ? new Intl.NumberFormat("en", {
                                  notation: "standard",
                                  maximumFractionDigits: 2,
                                }).format(data?.plan?.price || 0) +
                                " " +
                                (loggedInUserData?.business?.currency || "")
                              : "---",
                          }).map(([key, value], index, arr) => (
                            <Box
                              key={key}
                              className="flex justify-between items-center gap-[12px]"
                              style={
                                index < arr.length - 1
                                  ? {
                                      borderBottom: "1px solid #C5C6CD4D",
                                      paddingBottom: "5px",
                                    }
                                  : {}
                              }
                            >
                              <Box className="text-[12px] text-[#6B7280] font-medium text-left">
                                {t(key)}
                              </Box>
                              <Box className="text-[12px] text-[#111827] font-semibold text-right">
                                {value}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Button
                          fullWidth
                          disableElevation
                          className="normal-case font-Saira font-medium text-[10px] rounded-[8px] py-[8px] tracking-[0.6px]"
                          style={{
                            backgroundColor: themeColors?.primary,
                            color: "#fff",
                          }}
                          onClick={() => setBillingHistoryModal(true)}
                        >
                          {t("viewBillingHistory")}
                        </Button>
                      </Box>
                      {!feature2201 ? (
                        <Tooltip title={t("noRecordFound")} followCursor>
                          <Box
                            className="bg-white font-Saira rounded-[8px] border border-[#E5E7EB] px-[16px] flex items-center h-[67px] shrink-0"
                            sx={{ opacity: 0.6 }}
                          >
                            <Skeleton
                              animation={false}
                              sx={{
                                backgroundColor: themeColors?.secondary,
                                width: "100%",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box className="bg-white font-Saira rounded-[8px] border border-[#E5E7EB] px-[16px] flex items-center gap-[12px] h-[67px] shrink-0">
                          <Box className="w-[40px] h-[40px] rounded-[8px] border border-[#E5E7EB] flex items-center justify-center shrink-0">
                            <CloudOutlinedIcon
                              sx={{ fontSize: 21, color: "blue" }}
                            />
                          </Box>
                          <Box className="flex flex-col gap-[2px] min-w-0">
                            <Typography className="text-[12px] text-[#6B7280] font-medium leading-none font-Saira text-left">
                              {t(feature2201?.name || "---")}
                            </Typography>
                            <Typography className="font-semibold text-[14px] text-[#111827] leading-tight font-Saira text-left">
                              {`${formatBytesToGb(Number(feature2201?.usage ?? 0))} GB / ${
                                feature2201?.maxLimit === -1
                                  ? t("Unlimited")
                                  : formatBytesToGb(
                                      Number(feature2201?.maxLimit ?? 0),
                                    ) + " GB"
                              }`}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={1}>
                      {type1Features.length === 0 ? (
                        <Grid item xs={12}>
                          <Typography
                            className="font-Saira font-[500] text-[13px] italic text-center w-full py-6"
                            sx={{ color: themeColors?.primary }}
                          >
                            {t("noRecordFound")}
                          </Typography>
                        </Grid>
                      ) : (
                        topType1Features.map((feature: any) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            key={feature?.featureId || feature?.id}
                            sx={{ display: "flex" }}
                          >
                            <Box className="w-full">
                              {renderType1UsageWidget(feature)}
                            </Box>
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </Grid>
                  {bottomType1Features.length > 0 ? (
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        {bottomType1Features.map((feature: any) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={feature?.featureId || feature?.id}
                            sx={{ display: "flex" }}
                          >
                            <Box className="w-full">
                              {renderType1UsageWidget(feature)}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  ) : null}
                  {subscriptionControls && (
                    <>
                      <Grid item xs={12} className="mt-[20px]">
                        <Box className="flex items-center gap-[8px]">
                          <Typography className="font-Saira font-semibold text-[15px] text-[#111827] leading-none">
                            {t("subscriptionControls")}
                          </Typography>
                          <ShieldOutlinedIcon sx={{ fontSize: 19 }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Box
                          className={`bg-white rounded-[8px] border border-[#E5E7EB] p-[20px] flex flex-col gap-[12px] ${isLargeScreen ? "h-[158px]" : ""}`}
                        >
                          <Typography className="font-Saira font-semibold text-[15px] text-[#111827] leading-tight text-left">
                            {t("changePlan")}
                          </Typography>
                          <Typography className="font-Saira text-[12px] text-[#6B7280] font-medium leading-snug text-left flex-1">
                            {t("changePlanDescription")}
                          </Typography>
                          {!isSuspended && !isCancelled ? (
                            <Button
                              disableRipple
                              disableElevation
                              className="normal-case font-Saira font-semibold text-[12px] p-0 min-w-0 justify-start gap-[6px] relative top-[-4px]"
                              style={{
                                color: themeColors?.primary,
                                backgroundColor: "transparent",
                              }}
                            onClick={() => setChangePlanModal(true)}
                          >
                            {t("changePlans")}
                            <ArrowForward sx={{ fontSize: 15 }} />
                          </Button>
                        ) : null}
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Box
                        className={`bg-white rounded-[8px] border border-[#E5E7EB] p-[20px] flex flex-col gap-[12px] ${isLargeScreen ? "h-[158px]" : ""}`}
                      >
                        <Typography className="font-Saira font-semibold text-[15px] text-[#111827] leading-tight text-left">
                          {t("suspendAccount")}
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-[#6B7280] font-medium leading-snug text-left flex-1">
                          {isSuspended
                            ? t("resumeAccountDescription")
                            : t("suspendAccountDescription")}
                        </Typography>
                        {!isCancelled ? (
                          <Button
                            disableRipple
                            disableElevation
                            disabled={suspending}
                            className="normal-case font-Saira font-semibold text-[12px] flex items-center justify-start gap-[4px]"
                            style={{
                              color: isSuspended
                                ? themeColors?.primary
                                : "#BA1A1A",
                              backgroundColor: "transparent",
                            }}
                            onClick={() =>
                              isSuspended
                                ? handleResumeNow()
                                : setSuspendModal(true)
                            }
                          >
                            {suspending ? (
                              <CircularProgress
                                sx={{
                                  color: isSuspended
                                    ? themeColors?.primary
                                    : "#BA1A1A",
                                }}
                                size={16}
                              />
                            ) : isSuspended ? (
                              <>
                                <span>{t("resumePlan")}</span>
                                <PlayArrow
                                  sx={{ fontSize: 15, marginTop: "-2px" }}
                                />
                              </>
                            ) : (
                              <>
                                <span>{t("suspendAccount")}</span>
                                <Pause
                                  sx={{ fontSize: 15, marginTop: "-2px" }}
                                />
                              </>
                            )}
                          </Button>
                        ) : null}
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Box
                        className={`rounded-[8px] border border-[#F5C2C2] bg-[#FEF2F2] p-[20px] flex flex-col gap-[12px] ${isLargeScreen ? "h-[158px]" : ""}`}
                      >
                        <Typography className="font-Saira font-semibold text-[15px] text-[#BA1A1A] leading-tight text-left">
                          {t("cancelSubscription")}
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-[#6B7280] font-medium leading-snug text-left flex-1">
                          {t("cancelSubscriptionDescription")}
                        </Typography>
                        {!isCancelled ? (
                          <Button
                            disableRipple
                            disableElevation
                            disabled={suspending}
                            className="normal-case font-Saira font-semibold text-[11px] rounded-[6px] py-[8px] px-[16px] self-start"
                            style={{
                              backgroundColor: "#BA1A1A",
                              color: "#fff",
                            }}
                            onClick={() => setCancelModal(true)}
                          >
                            {t("cancelSubscription")}
                          </Button>
                        ) : null}
                      </Box>
                    </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Box>
          }
        />
      </div>

      {billingHistoryModal && (
        <FormDialog
          open={billingHistoryModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick")
              setBillingHistoryModal(false);
          }}
          title={t("BillingHistory")}
          createPopup={true}
          hideActions={true}
          fullWidth
          content={
            <Box>
              <SubscriptionPlanHistory businessPlanId={data?.id || null} />
            </Box>
          }
        />
      )}
      {changePlanModal && (
        <FormDialog
          open={changePlanModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick") setChangePlanModal(false);
          }}
          title={t("changePlan")}
          createPopup={true}
          hideActions={true}
          content={
            <Box className="w-[500px]">
              <SubscriptionPlanChange
                setChangePlanModal={setChangePlanModal}
                planRefetch={fetchData}
                subscriptionPlanId={data?.plan?.id || null}
                businessId={businessId || null}
              />
            </Box>
          }
        />
      )}
      {suspendModal && (
        <FormDialog
          open={suspendModal}
          onClose={() => setSuspendModal(false)}
          title={t(isSuspended ? "resumePlan" : "suspendAccount")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setSuspendModal(false);
            handleSuspendNow(5);
          }}
          cancelBtnText="No"
          submitButtonText="Yes"
        />
      )}
      {cancelModal && (
        <FormDialog
          open={cancelModal}
          onClose={() => setCancelModal(false)}
          title={t("cancelSubscription")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  {t("Are you sure to cancel the subscription?")}
                </Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setCancelModal(false);
            handleSuspendNow(4);
          }}
          cancelBtnText="No"
          submitButtonText="Yes"
        />
      )}
    </>
  );
}
